import { createContext, type ReactNode, useContext } from 'react';
import { EnvUnsupported } from '../components/EnvUnsupported';
import { isTelegramEnvironment } from '../lib/telegram';
import {
  AnonymousAuthProvider,
  useAnonymousAuth,
} from './AnonymousAuthContext';
import { TelegramAuthProvider, useTelegramAuth } from './TelegramAuthContext';

// Unified User type that works for both auth methods
interface User {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  image: string | null;
  telegramId?: string | null;
  telegramUsername?: string | null;
  role?: string;
  isAnonymous?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  role: string | null;
  isGuest: boolean;
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
  signInWithTelegram: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ALLOW_NON_TELEGRAM =
  import.meta.env.VITE_ALLOW_NON_TELEGRAM === 'true' || import.meta.env.DEV;

// Inner component that consumes the appropriate auth context
function AuthContextConsumer({ children }: { children: ReactNode }) {
  const inTelegram = isTelegramEnvironment();
  // Use Telegram auth in Telegram environment
  if (inTelegram) {
    return <TelegramAuthConsumer>{children}</TelegramAuthConsumer>;
  }

  // Use Anonymous auth for non-Telegram web users
  return <AnonymousAuthConsumer>{children}</AnonymousAuthConsumer>;
}

// Telegram auth consumer
function TelegramAuthConsumer({ children }: { children: ReactNode }) {
  const telegramAuth = useTelegramAuth();

  const value: AuthContextValue = {
    user: telegramAuth.user,
    token: telegramAuth.token,
    isLoading: telegramAuth.isLoading,
    isAuthenticated: !!telegramAuth.user,
    role: telegramAuth.user?.role ?? null,
    isGuest: false,
    signOut: async () => {
      localStorage.removeItem('token');
    },
    signInWithTelegram: telegramAuth.signInWithLoginButton,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Anonymous auth consumer
function AnonymousAuthConsumer({ children }: { children: ReactNode }) {
  const anonymousAuth = useAnonymousAuth();

  // Login via the Telegram Login Widget is handled by UserLoginModal directly.
  // This stub keeps the AuthContextValue interface intact.
  const signInWithTelegram = async () => {
    // no-op: widget auth is managed by UserLoginModal via authClient.initTelegramWidget
  };

  const value: AuthContextValue = {
    user: anonymousAuth.user,
    token: null,
    isLoading: anonymousAuth.isLoading,
    isAuthenticated: anonymousAuth.isAuthenticated,
    role: anonymousAuth.user?.role ?? null,
    isGuest: true,
    signOut: anonymousAuth.signOut,
    signInWithTelegram,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Main AuthProvider that wraps with the appropriate provider based on environment
export function AuthProvider({ children }: { children: ReactNode }) {
  const inTelegram = isTelegramEnvironment();

  if (!inTelegram && !ALLOW_NON_TELEGRAM) {
    return <EnvUnsupported />;
  }

  // If in Telegram, use Telegram auth
  if (inTelegram) {
    return (
      <TelegramAuthProvider>
        <AuthContextConsumer>{children}</AuthContextConsumer>
      </TelegramAuthProvider>
    );
  }

  // If not in Telegram and non-Telegram is allowed, use Anonymous auth
  return (
    <AnonymousAuthProvider>
      <AuthContextConsumer>{children}</AuthContextConsumer>
    </AnonymousAuthProvider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
